import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Upload,
  Select,
  Checkbox,
  InputNumber,
  message,
  Row,
  Col,
  Typography,
  Alert,
  Tag,
  Icon
} from 'antd';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const { Option } = Select;
const { Title, Text } = Typography;

/**
 * Highest Density Interval (HDI) calculation
 * Returns: (lower, upper, count_in_interval)
 */
const highestDensityInterval = (values, coverage, centerHint = null) => {
  if (values.length === 0) {
    return [NaN, NaN, 0];
  }

  const n = values.length;
  let k = Math.ceil(coverage * n);
  k = Math.min(Math.max(k, 1), n); // clamp to [1, n]

  const vSorted = [...values].sort((a, b) => a - b);
  let bestI = 0;
  let bestWidth = Infinity;
  const ties = [];

  for (let i = 0; i <= n - k; i++) {
    const width = vSorted[i + k - 1] - vSorted[i];
    if (width < bestWidth - 1e-15) {
      bestWidth = width;
      bestI = i;
      ties.length = 0;
      ties.push(i);
    } else if (Math.abs(width - bestWidth) <= 1e-15) {
      ties.push(i);
    }
  }

  // Tie-break using the interval whose midpoint is closest to centerHint (median)
  if (centerHint === null) {
    centerHint = median(values);
  }
  if (ties.length > 1) {
    bestI = ties.reduce((best, idx) => {
      const midpoint = (vSorted[idx] + vSorted[idx + k - 1]) / 2.0;
      const bestMidpoint = (vSorted[best] + vSorted[best + k - 1]) / 2.0;
      return Math.abs(midpoint - centerHint) < Math.abs(bestMidpoint - centerHint) ? idx : best;
    }, ties[0]);
  }

  const lower = vSorted[bestI];
  const upper = vSorted[bestI + k - 1];
  const count = values.filter(v => v >= lower && v <= upper).length;
  return [lower, upper, count];
};

/**
 * Adaptive coverage based on group size
 */
const adaptiveCoverage = (n) => {
  if (n < 20) return 0.30;
  if (n < 50) return 0.40;
  return 0.50;
};

/**
 * Calculate median
 */
const median = (arr) => {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

/**
 * Calculate trimmed mean (10%)
 */
const trimmedMean = (arr, trimPercent = 0.10) => {
  if (arr.length === 0) return NaN;
  const sorted = [...arr].sort((a, b) => a - b);
  const trimCount = Math.floor(arr.length * trimPercent);
  const trimmed = sorted.slice(trimCount, arr.length - trimCount);
  if (trimmed.length === 0) return NaN;
  return trimmed.reduce((sum, val) => sum + val, 0) / trimmed.length;
};

/**
 * Compute stats using HDI with adaptive coverage
 */
const computeStatsHDIAdaptive = (series, dropZero) => {
  const nBefore = series.length;
  
  // Convert to numeric and drop NaN
  let numericValues = series
    .map(val => {
      const num = parseFloat(val);
      return isNaN(num) ? null : num;
    })
    .filter(val => val !== null);

  if (dropZero) {
    numericValues = numericValues.filter(val => val !== 0);
  }

  if (numericValues.length === 0) {
    return {
      n_before: nBefore,
      n_numeric: 0,
      dropped_zeros: dropZero,
      coverage_used: NaN,
      hdi_lower: NaN,
      hdi_upper: NaN,
      n_in_hdi: 0,
      hdi_average: NaN,
      median: NaN,
      trimmed_mean_10pct: NaN,
    };
  }

  const n = numericValues.length;
  const coverage = adaptiveCoverage(n);
  const centerHint = median(numericValues);
  const [lower, upper, cnt] = highestDensityInterval(numericValues, coverage, centerHint);

  const windowVals = numericValues.filter(v => v >= lower && v <= upper);
  const hdiAvg = windowVals.length > 0
    ? windowVals.reduce((sum, val) => sum + val, 0) / windowVals.length
    : NaN;
  const medianVal = median(numericValues);
  const trimmed = trimmedMean(numericValues, 0.10);

  return {
    n_before: nBefore,
    n_numeric: n,
    dropped_zeros: dropZero,
    coverage_used: coverage,
    hdi_lower: lower,
    hdi_upper: upper,
    n_in_hdi: cnt,
    hdi_average: hdiAvg,
    median: medianVal,
    trimmed_mean_10pct: trimmed,
  };
};

/**
 * Resolve column name (exact, case-insensitive, or partial match)
 */
const resolveColumn = (columns, colIn) => {
  if (!colIn) {
    throw new Error('Empty column name');
  }
  
  // Exact match
  if (columns.includes(colIn)) {
    return colIn;
  }
  
  // Case-insensitive match
  const lowerMap = {};
  columns.forEach(c => { lowerMap[c.toLowerCase()] = c; });
  if (lowerMap[colIn.toLowerCase()]) {
    return lowerMap[colIn.toLowerCase()];
  }
  
  // Partial match
  const candidates = columns.filter(c => 
    c.toLowerCase().includes(colIn.toLowerCase())
  );
  
  if (candidates.length === 1) {
    return candidates[0];
  } else if (candidates.length > 1) {
    throw new Error(`Multiple matches found for "${colIn}": ${candidates.join(', ')}`);
  } else {
    throw new Error(`Column "${colIn}" not found. Available: ${columns.join(', ')}`);
  }
};

function ConsumptionRateAnalyzer() {
  const [fileData, setFileData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [modelColumn, setModelColumn] = useState(null);
  const [metricColumns, setMetricColumns] = useState([]);
  const [dropZeros, setDropZeros] = useState(false);
  const [minRows, setMinRows] = useState(5);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assetTypeColumn, setAssetTypeColumn] = useState(null);
  const [assetSubTypeColumn, setAssetSubTypeColumn] = useState(null);
  const [filterAssetModel, setFilterAssetModel] = useState(null);
  const [filterAssetType, setFilterAssetType] = useState(null);
  const [filterAssetSubType, setFilterAssetSubType] = useState(null);
  const [resultRowDataMap, setResultRowDataMap] = useState({}); // Map to store original rows for each result

  const handleFileUpload = (file) => {
    setLoading(true);
    const reader = new FileReader();
    const fileName = file.name.toLowerCase();
    
    reader.onload = (e) => {
      try {
        let data = null;
        
        if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
          // Excel file
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const firstSheet = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheet];
          data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (data.length === 0) {
            throw new Error('Excel file is empty');
          }
          
          // Convert to object array
          const headers = data[0];
          const rows = data.slice(1).map(row => {
            const obj = {};
            headers.forEach((header, idx) => {
              obj[header] = row[idx] !== undefined ? row[idx] : '';
            });
            return obj;
          });
          data = rows;
          
          if (data && data.length > 0) {
            setFileData(data);
            setColumns(Object.keys(data[0]));
            message.success(`Loaded ${data.length} rows from Excel file`);
          } else {
            throw new Error('No data found in file');
          }
        } else if (fileName.endsWith('.csv')) {
          // CSV file - handled in Papa.parse callback
          Papa.parse(e.target.result, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              if (results.errors.length > 0) {
                console.warn('CSV parsing warnings:', results.errors);
              }
              if (results.data && results.data.length > 0) {
                setFileData(results.data);
                setColumns(Object.keys(results.data[0] || {}));
                message.success(`Loaded ${results.data.length} rows from CSV`);
              } else {
                message.error('No data found in CSV file');
                setFileData(null);
                setColumns([]);
              }
              setLoading(false);
            },
            error: (error) => {
              message.error(`Error parsing CSV: ${error.message}`);
              setFileData(null);
              setColumns([]);
              setLoading(false);
            }
          });
          return; // CSV parsing is async, handled in callbacks
        } else {
          throw new Error('Unsupported file type. Please upload CSV or Excel file.');
        }
      } catch (error) {
        message.error(`Error loading file: ${error.message}`);
        setFileData(null);
        setColumns([]);
        setLoading(false);
      }
    };
    
    if (fileName.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
    
    return false; // Prevent auto upload
  };

  const processData = () => {
    if (!fileData || fileData.length === 0) {
      message.warning('Please upload a file first');
      return;
    }
    
    if (!modelColumn) {
      message.warning('Please select a model column');
      return;
    }
    
    if (metricColumns.length === 0) {
      message.warning('Please select at least one metric column');
      return;
    }

    setLoading(true);
    try {
      // Resolve model column
      let resolvedModelCol;
      try {
        resolvedModelCol = resolveColumn(columns, modelColumn);
      } catch (error) {
        message.error(error.message);
        setLoading(false);
        return;
      }

      // Resolve metric columns
      const resolvedMetricCols = [];
      for (const metricCol of metricColumns) {
        try {
          const resolved = resolveColumn(columns, metricCol);
          resolvedMetricCols.push(resolved);
        } catch (error) {
          console.warn(`Skipping metric column "${metricCol}": ${error.message}`);
        }
      }

      if (resolvedMetricCols.length === 0) {
        message.error('None of the metric columns were found in the file');
        setLoading(false);
        return;
      }

      // Resolve asset type and asset sub type columns (optional)
      let resolvedAssetTypeCol = null;
      let resolvedAssetSubTypeCol = null;
      
      if (assetTypeColumn) {
        try {
          resolvedAssetTypeCol = resolveColumn(columns, assetTypeColumn);
        } catch (error) {
          console.warn(`Asset Type column not found: ${error.message}`);
        }
      }
      
      if (assetSubTypeColumn) {
        try {
          resolvedAssetSubTypeCol = resolveColumn(columns, assetSubTypeColumn);
        } catch (error) {
          console.warn(`Asset Sub Type column not found: ${error.message}`);
        }
      }

      // Group by model
      const grouped = {};
      fileData.forEach(row => {
        const modelValue = row[resolvedModelCol];
        if (modelValue === undefined || modelValue === null) return;
        
        const modelKey = String(modelValue);
        if (!grouped[modelKey]) {
          grouped[modelKey] = [];
        }
        grouped[modelKey].push(row);
      });

      const summaries = [];
      const rowDataMap = {}; // Map to store original rows for each result
      
      // Process each model group
      Object.entries(grouped).forEach(([modelValue, groupRows]) => {
        let anyMetricDone = false;
        
        // Extract asset type and asset sub type from first row (should be consistent within a model group)
        const firstRow = groupRows[0];
        const assetType = resolvedAssetTypeCol ? (firstRow[resolvedAssetTypeCol] || '') : '';
        const assetSubType = resolvedAssetSubTypeCol ? (firstRow[resolvedAssetSubTypeCol] || '') : '';
        
        resolvedMetricCols.forEach(metricCol => {
          const series = groupRows.map(row => row[metricCol]);
          let numericSeries = series
            .map(val => {
              const num = parseFloat(val);
              return isNaN(num) ? null : num;
            })
            .filter(val => val !== null);
          
          if (dropZeros) {
            numericSeries = numericSeries.filter(val => val !== 0);
          }
          
          const nNumeric = numericSeries.length;
          
          // Create a unique key for this result
          const resultKey = `${modelValue}_${metricCol}`;
          
          if (nNumeric < minRows) {
            summaries.push({
              model: modelValue,
              asset_type: assetType,
              asset_sub_type: assetSubType,
              metric_column: metricCol,
              n_before: series.length,
              n_numeric: nNumeric,
              dropped_zeros: dropZeros,
              coverage_used: NaN,
              hdi_lower: NaN,
              hdi_upper: NaN,
              n_in_hdi: 0,
              hdi_average: NaN,
              median: NaN,
              trimmed_mean_10pct: NaN,
              note: `Insufficient rows (<${minRows})`,
              resultKey: resultKey
            });
            // Store all original rows for this result
            rowDataMap[resultKey] = groupRows;
            return;
          }

          const stats = computeStatsHDIAdaptive(series, dropZeros);
          summaries.push({
            model: modelValue,
            asset_type: assetType,
            asset_sub_type: assetSubType,
            metric_column: metricCol,
            ...stats,
            note: '',
            resultKey: resultKey
          });
          // Store all original rows for this result
          rowDataMap[resultKey] = groupRows;
          anyMetricDone = true;
        });
      });

      setResults(summaries);
      setResultRowDataMap(rowDataMap);
      // Reset filters when new data is processed
      setFilterAssetModel(null);
      setFilterAssetType(null);
      setFilterAssetSubType(null);
      message.success(`Processed ${summaries.length} model-metric combinations`);
    } catch (error) {
      console.error('Error processing data:', error);
      message.error(`Error processing data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    const dataToDownload = filteredResults.length > 0 ? filteredResults : results;
    
    if (dataToDownload.length === 0) {
      message.warning('No results to download');
      return;
    }

    // Convert results to CSV
    const headers = Object.keys(dataToDownload[0]).filter(h => h !== 'resultKey'); // Exclude resultKey from CSV
    const csvRows = [
      headers.join(','),
      ...dataToDownload.map(row =>
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
            return 'n/a';
          }
          // Escape commas and quotes
          const str = String(value);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(',')
      )
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `hdi_adaptive_per_model_multi_metric_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('CSV file downloaded successfully');
  };

  const downloadRawDataForRow = (record) => {
    if (!record.resultKey || !resultRowDataMap[record.resultKey]) {
      message.warning('Raw data not available for this row');
      return;
    }

    const rawRows = resultRowDataMap[record.resultKey];
    
    if (rawRows.length === 0) {
      message.warning('No raw data available for this row');
      return;
    }

    // Get all column names from the original data
    const allColumns = columns.length > 0 ? columns : Object.keys(rawRows[0] || {});
    
    // Convert raw rows to CSV
    const csvRows = [
      allColumns.join(','),
      ...rawRows.map(row =>
        allColumns.map(col => {
          const value = row[col];
          if (value === null || value === undefined) {
            return '';
          }
          // Escape commas and quotes
          const str = String(value);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(',')
      )
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Create a descriptive filename
    const safeModel = String(record.model || 'unknown').replace(/[^a-z0-9]/gi, '_').substring(0, 30);
    const safeMetric = String(record.metric_column || 'unknown').replace(/[^a-z0-9]/gi, '_').substring(0, 30);
    const timestamp = new Date().getTime();
    const filename = `raw_data_${safeModel}_${safeMetric}_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success(`Downloaded ${rawRows.length} raw data rows`);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
      return 'n/a';
    }
    if (typeof value === 'number') {
      return value.toFixed(6);
    }
    return String(value);
  };

  // Get unique values for filters
  const getUniqueValues = (key) => {
    const values = new Set();
    results.forEach(r => {
      if (r[key] && r[key] !== '') {
        values.add(String(r[key]));
      }
    });
    return Array.from(values).sort();
  };

  // Filter results
  const filteredResults = results.filter(r => {
    if (filterAssetModel && r.model !== filterAssetModel) return false;
    if (filterAssetType && r.asset_type !== filterAssetType) return false;
    if (filterAssetSubType && r.asset_sub_type !== filterAssetSubType) return false;
    return true;
  });

  const tableColumns = [
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 150,
      fixed: 'left',
    },
    {
      title: 'Asset Type',
      dataIndex: 'asset_type',
      key: 'asset_type',
      width: 150,
    },
    {
      title: 'Asset Sub Type',
      dataIndex: 'asset_sub_type',
      key: 'asset_sub_type',
      width: 150,
    },
    {
      title: 'Metric Column',
      dataIndex: 'metric_column',
      key: 'metric_column',
      width: 150,
    },
    {
      title: 'N Before',
      dataIndex: 'n_before',
      key: 'n_before',
      width: 100,
      align: 'right',
    },
    {
      title: 'N Numeric',
      dataIndex: 'n_numeric',
      key: 'n_numeric',
      width: 100,
      align: 'right',
    },
    {
      title: 'Dropped Zeros',
      dataIndex: 'dropped_zeros',
      key: 'dropped_zeros',
      width: 120,
      render: (value) => value ? 'Yes' : 'No',
    },
    {
      title: 'Coverage Used',
      dataIndex: 'coverage_used',
      key: 'coverage_used',
      width: 130,
      align: 'right',
      render: formatValue,
    },
    {
      title: 'HDI Lower',
      dataIndex: 'hdi_lower',
      key: 'hdi_lower',
      width: 120,
      align: 'right',
      render: formatValue,
    },
    {
      title: 'HDI Upper',
      dataIndex: 'hdi_upper',
      key: 'hdi_upper',
      width: 120,
      align: 'right',
      render: formatValue,
    },
    {
      title: 'N in HDI',
      dataIndex: 'n_in_hdi',
      key: 'n_in_hdi',
      width: 100,
      align: 'right',
    },
    {
      title: 'HDI Average',
      dataIndex: 'hdi_average',
      key: 'hdi_average',
      width: 130,
      align: 'right',
      render: (value, record) => (
        <strong 
          style={{ 
            color: '#1890ff', 
            cursor: resultRowDataMap[record.resultKey] ? 'pointer' : 'default',
            textDecoration: resultRowDataMap[record.resultKey] ? 'underline' : 'none'
          }}
          onClick={() => {
            if (resultRowDataMap[record.resultKey]) {
              downloadRawDataForRow(record);
            }
          }}
          title={resultRowDataMap[record.resultKey] ? 'Click to download raw data' : ''}
        >
          {formatValue(value)}
        </strong>
      ),
    },
    {
      title: 'Median',
      dataIndex: 'median',
      key: 'median',
      width: 120,
      align: 'right',
      render: formatValue,
    },
    {
      title: '10% Trimmed Mean',
      dataIndex: 'trimmed_mean_10pct',
      key: 'trimmed_mean_10pct',
      width: 150,
      align: 'right',
      render: formatValue,
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      width: 200,
      render: (text) => text ? <span style={{ color: '#ff4d4f' }}>{text}</span> : '-',
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card
        title={
          <Title level={3} style={{ margin: 0 }}>
            Consumption Rate Analyzer (HDI with Adaptive Coverage)
          </Title>
        }
      >
        <Alert
          message="Highest Density Interval (HDI) Analysis"
          description="This tool calculates consumption rate averages using the Highest Density Interval method with adaptive coverage. Coverage adapts based on data size: n<20 (30%), 20≤n<50 (40%), n≥50 (50%)."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card size="small" title="File Upload">
              <Upload
                accept=".csv,.xlsx,.xls"
                beforeUpload={handleFileUpload}
                showUploadList={false}
              >
                <Button icon="upload" loading={loading}>
                  Upload CSV or Excel File
                </Button>
              </Upload>
              {fileData && (
                <Text style={{ marginLeft: 16, color: '#52c41a' }}>
                  ✓ Loaded {fileData.length} rows, {columns.length} columns
                </Text>
              )}
            </Card>
          </Col>
        </Row>

        {fileData && columns.length > 0 && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Card size="small" title="Configuration">
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Model Column:</Text>
                    <Select
                      style={{ width: '100%', marginTop: 8 }}
                      placeholder="Select model column"
                      value={modelColumn}
                      onChange={setModelColumn}
                      showSearch
                      filterOption={(input, option) => {
                        if (!option || !option.props) return false;
                        const text = String(option.props.children || option.props.value || '');
                        return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      }}
                    >
                      {columns.map(col => (
                        <Option key={col} value={col}>{col}</Option>
                      ))}
                    </Select>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Metric Columns:</Text>
                    <Select
                      mode="multiple"
                      style={{ width: '100%', marginTop: 8 }}
                      placeholder="Select metric columns (comma-separated)"
                      value={metricColumns}
                      onChange={setMetricColumns}
                      showSearch
                      filterOption={(input, option) => {
                        if (!option || !option.props) return false;
                        const text = String(option.props.children || option.props.value || '');
                        return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      }}
                    >
                      {columns.map(col => (
                        <Option key={col} value={col}>{col}</Option>
                      ))}
                    </Select>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Asset Type Column (Optional):</Text>
                    <Select
                      style={{ width: '100%', marginTop: 8 }}
                      placeholder="Select asset type column (optional)"
                      value={assetTypeColumn}
                      onChange={setAssetTypeColumn}
                      allowClear
                      showSearch
                      filterOption={(input, option) => {
                        if (!option || !option.props) return false;
                        const text = String(option.props.children || option.props.value || '');
                        return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      }}
                    >
                      {columns.map(col => (
                        <Option key={col} value={col}>{col}</Option>
                      ))}
                    </Select>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Asset Sub Type Column (Optional):</Text>
                    <Select
                      style={{ width: '100%', marginTop: 8 }}
                      placeholder="Select asset sub type column (optional)"
                      value={assetSubTypeColumn}
                      onChange={setAssetSubTypeColumn}
                      allowClear
                      showSearch
                      filterOption={(input, option) => {
                        if (!option || !option.props) return false;
                        const text = String(option.props.children || option.props.value || '');
                        return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      }}
                    >
                      {columns.map(col => (
                        <Option key={col} value={col}>{col}</Option>
                      ))}
                    </Select>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Checkbox
                      checked={dropZeros}
                      onChange={(e) => setDropZeros(e.target.checked)}
                    >
                      Drop zeros
                    </Checkbox>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Minimum rows per (model, metric):</Text>
                    <InputNumber
                      style={{ width: '100%', marginTop: 8 }}
                      min={1}
                      value={minRows}
                      onChange={setMinRows}
                      placeholder="Default: 5"
                    />
                  </div>

                  <Button
                    type="primary"
                    onClick={processData}
                    loading={loading}
                    block
                    style={{ marginTop: 16 }}
                  >
                    Process Data
                  </Button>
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small" title="Available Columns">
                  <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {columns.map((col, idx) => (
                      <Tag key={idx} style={{ marginBottom: 8 }}>
                        {col}
                      </Tag>
                    ))}
                  </div>
                </Card>
              </Col>
            </Row>

            {results.length > 0 && (
              <>
                <Card size="small" title="Filters" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text strong>Filter by Asset Model:</Text>
                      <Select
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="All Models"
                        value={filterAssetModel}
                        onChange={setFilterAssetModel}
                        allowClear
                        showSearch
                        filterOption={(input, option) => {
                          const text = option.props ? (option.props.children || option.props.value || '') : (option.children || option.value || '');
                          return String(text).toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                      >
                        {getUniqueValues('model').map(val => (
                          <Option key={val} value={val}>{val}</Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={8}>
                      <Text strong>Filter by Asset Type:</Text>
                      <Select
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="All Asset Types"
                        value={filterAssetType}
                        onChange={setFilterAssetType}
                        allowClear
                        showSearch
                        filterOption={(input, option) => {
                          const text = option.props ? (option.props.children || option.props.value || '') : (option.children || option.value || '');
                          return String(text).toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                      >
                        {getUniqueValues('asset_type').map(val => (
                          <Option key={val} value={val}>{val}</Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={8}>
                      <Text strong>Filter by Asset Sub Type:</Text>
                      <Select
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="All Asset Sub Types"
                        value={filterAssetSubType}
                        onChange={setFilterAssetSubType}
                        allowClear
                        showSearch
                        filterOption={(input, option) => {
                          const text = option.props ? (option.props.children || option.props.value || '') : (option.children || option.value || '');
                          return String(text).toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                      >
                        {getUniqueValues('asset_sub_type').map(val => (
                          <Option key={val} value={val}>{val}</Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                </Card>
                <Card
                  title={`Results ${filteredResults.length !== results.length ? `(${filteredResults.length} of ${results.length})` : `(${results.length})`}`}
                  extra={
                    <Button
                      icon="download"
                      onClick={downloadCSV}
                      type="primary"
                    >
                      Download CSV
                    </Button>
                  }
                >
                  <Table
                    columns={tableColumns}
                    dataSource={filteredResults.map((r, idx) => ({ ...r, key: idx }))}
                    scroll={{ x: 1700, y: 600 }}
                    pagination={{
                      pageSize: 50,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} results`
                    }}
                  />
                </Card>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default ConsumptionRateAnalyzer;











